import { auth } from '../firebase'; // Import auth instance

/**
 * Usage Analytics Service for Dashboard
 * 
 * Connects to the new usage analytics module cloud functions via HTTP
 * to get comprehensive agent usage metrics and analytics
 */
class UsageAnalyticsService {
  constructor() {
    // Base URL for usage analytics functions (following agents-joinAgentMeeting pattern)
    this.baseUrl = 'https://us-central1-polletask-dev.cloudfunctions.net';
  }

  /**
   * Make an authenticated HTTP request to a cloud function
   * @param {string} functionName - Name of the function
   * @param {Object} data - Request body
   * @returns {Promise<Object>} Response data
   */
  async callFunction(functionName, data) {
    try {
      // Get the current user's ID token for authentication
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;

      const response = await fetch(`${this.baseUrl}/${functionName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`❌ Error calling ${functionName}:`, error);
      throw error;
    }
  }

  /**
   * Get comprehensive dashboard metrics for an agent
   * @param {string} agentId - The agent ID
   * @param {Date} startDate - Start date for metrics
   * @param {Date} endDate - End date for metrics
   * @returns {Promise<Object>} Dashboard metrics
   */
  async getDashboardMetrics(agentId, startDate, endDate) {
    try {
      console.log('🔍 getDashboardMetrics called with:', { agentId, startDate, endDate, startDateType: typeof startDate, endDateType: typeof endDate });
      
      // Validate parameters
      if (!agentId) {
        throw new Error('agentId is required');
      }
      if (!startDate || !(startDate instanceof Date)) {
        console.error('❌ startDate validation failed:', startDate);
        throw new Error('startDate must be a valid Date object');
      }
      if (!endDate || !(endDate instanceof Date)) {
        console.error('❌ endDate validation failed:', endDate);
        throw new Error('endDate must be a valid Date object');
      }

      const requestPayload = {
        agentId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      };
      
      console.log(`📊 USAGE ANALYTICS: Fetching dashboard metrics for agent ${agentId}`, requestPayload);

      const result = await this.callFunction('usage-getDashboardMetrics', requestPayload);

      if (result && result.success) {
        const metrics = result.data;
        console.log(`📊 USAGE ANALYTICS: Received dashboard metrics:`, metrics);
        return metrics;
      } else {
        throw new Error(result?.error || 'Failed to load dashboard metrics');
      }
    } catch (error) {
      console.error(`📊 USAGE ANALYTICS: Error fetching dashboard metrics:`, error);
      throw error;
    }
  }

  /**
   * Get task analytics for a specific agent and time range
   * @param {string} agentId - The agent ID
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<Object>} Task analytics
   */
  async getTaskAnalytics(agentId, startDate, endDate) {
    try {
      // Validate parameters
      if (!agentId) {
        throw new Error('agentId is required');
      }
      if (!startDate || !(startDate instanceof Date)) {
        throw new Error('startDate must be a valid Date object');
      }
      if (!endDate || !(endDate instanceof Date)) {
        throw new Error('endDate must be a valid Date object');
      }

      const result = await this.callFunction('usage-getTaskAnalytics', {
        agentId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });

      if (result && result.success) {
        return result.data;
      } else {
        throw new Error(result?.error || 'Failed to load task analytics');
      }
    } catch (error) {
      console.error(`📊 USAGE ANALYTICS: Error fetching task analytics:`, error);
      throw error;
    }
  }

  /**
   * Get meeting analytics for a specific agent and time range
   * @param {string} agentId - The agent ID
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<Object>} Meeting analytics
   */
  async getMeetingAnalytics(agentId, startDate, endDate) {
    try {
      // Validate parameters
      if (!agentId) {
        throw new Error('agentId is required');
      }
      if (!startDate || !(startDate instanceof Date)) {
        throw new Error('startDate must be a valid Date object');
      }
      if (!endDate || !(endDate instanceof Date)) {
        throw new Error('endDate must be a valid Date object');
      }

      const result = await this.callFunction('usage-getMeetingAnalytics', {
        agentId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });

      if (result && result.success) {
        return result.data;
      } else {
        throw new Error(result?.error || 'Failed to load meeting analytics');
      }
    } catch (error) {
      console.error(`📊 USAGE ANALYTICS: Error fetching meeting analytics:`, error);
      throw error;
    }
  }

  /**
   * Get tool usage statistics
   * @param {string} agentId - The agent ID
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @param {number} limit - Maximum number of tools to return
   * @returns {Promise<Array>} Tool usage stats
   */
  async getToolUsageStats(agentId, startDate, endDate, limit = 10) {
    try {
      const result = await this.callFunction('usage-getToolUsageStats', {
        agentId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        limit,
      });

      if (result && result.success) {
        return result.data;
      } else {
        throw new Error(result?.error || 'Failed to load tool usage stats');
      }
    } catch (error) {
      console.error(`📊 USAGE ANALYTICS: Error fetching tool usage stats:`, error);
      throw error;
    }
  }

  /**
   * Get recent task activity
   * @param {string} agentId - The agent ID
   * @param {number} limit - Maximum number of tasks to return
   * @returns {Promise<Array>} Recent task activities
   */
  async getRecentTaskActivity(agentId, limit = 10) {
    try {
      const result = await this.callFunction('usage-getRecentTaskActivity', {
        agentId,
        limit,
      });

      if (result && result.success) {
        return result.data;
      } else {
        throw new Error(result?.error || 'Failed to load recent task activity');
      }
    } catch (error) {
      console.error(`📊 USAGE ANALYTICS: Error fetching recent task activity:`, error);
      throw error;
    }
  }

  /**
   * Get task completion trends
   * @param {string} agentId - The agent ID
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @param {string} granularity - 'hour', 'day', 'week', 'month'
   * @returns {Promise<Array>} Trend data points
   */
  async getTaskCompletionTrends(agentId, startDate, endDate, granularity = 'day') {
    try {
      const result = await this.callFunction('usage-getTaskCompletionTrends', {
        agentId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        granularity,
      });

      if (result && result.success) {
        return result.data;
      } else {
        throw new Error(result?.error || 'Failed to load task completion trends');
      }
    } catch (error) {
      console.error(`📊 USAGE ANALYTICS: Error fetching task completion trends:`, error);
      throw error;
    }
  }

  /**
   * Get agent entitlements (what the agent can do)
   * @param {string} agentId - The agent ID
   * @returns {Promise<Object>} Agent entitlements
   */
  async getEntitlements(agentId) {
    try {
      const result = await this.callFunction('usage-getEntitlements', {
        agentId,
      });

      if (result && result.success) {
        return result.data;
      } else {
        throw new Error(result?.error || 'Failed to load entitlements');
      }
    } catch (error) {
      console.error(`📊 USAGE ANALYTICS: Error fetching entitlements:`, error);
      throw error;
    }
  }

  /**
   * Convert time range string to days
   * @param {string} timeRange - Time range string (24h, 7d, 30d, 90d)
   * @returns {number} Number of days
   */
  timeRangeToDays(timeRange) {
    switch (timeRange) {
      case '24h':
      case '1d':
        return 1;
      case '7d':
        return 7;
      case '30d':
        return 30;
      case '90d':
        return 90;
      default:
        return 7; // Default to 7 days
    }
  }

  /**
   * Get empty/default analytics data structure
   * @param {string} agentId - Optional agent ID
   * @returns {Object} Empty analytics data
   */
  getEmptyAnalytics(agentId = null) {
    return {
      agentId: agentId,
      timeRange: '7d',
      totalTasks: 0,
      taskSuccessRate: 0,
      avgTaskCompletionTimeMs: 0,
      totalCalls: 0,
      avgCallDurationSeconds: 0,
      totalCallDurationSeconds: 0,
      mostUsedTools: [],
      recentTasks: [],
      taskCompletionTrends: [],
    };
  }
}

// Export singleton instance
export const usageAnalyticsService = new UsageAnalyticsService();
export default usageAnalyticsService;

